// src/pages/Dashboard.tsx
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";

import "survey-analytics/survey.analytics.css";
import { Model } from "survey-core";
import {
  VisualizationPanel,
  IVisualizationPanelOptions,
  VisualizerBase,
  PlotlySetup,
} from "survey-analytics";

import { Box, Typography, CircularProgress } from "@mui/material";
import { useSurveyAccess } from "../hooks/useSurveyAccess";
import PageHeader from "../components/PageHeader";
import { EDGE_COLOR_PALETTE } from "../utils/edgeColors";

type SurveyElement = {
  name?: string;
  type?: string;
  title?: string;
  elements?: SurveyElement[];
  templateElements?: SurveyElement[];
  choices?: Array<string | number | { value: string | number; text?: string }>;
  rateValues?: Array<string | number | { value: string | number; text?: string }>;
};

const findQuestionDef = (survey: any, name: string): SurveyElement | undefined => {
  if (!survey?.pages) return undefined;
  const stack: SurveyElement[] = [];
  survey.pages.forEach((p: any) => p.elements?.forEach((e: SurveyElement) => stack.push(e)));
  while (stack.length) {
    const el = stack.pop()!;
    if (el?.name === name) return el;
    el?.elements?.forEach((child) => stack.push(child));
    el?.templateElements?.forEach((child) => stack.push(child));
  }
  return undefined;
};

const extractChoiceOrder = (el: SurveyElement | undefined): string[] => {
  const list = el?.choices ?? el?.rateValues ?? [];
  return list
    .map((c) => {
      if (typeof c === "string" || typeof c === "number") return String(c);
      if (c && typeof c === "object" && "value" in c) return String(c.text ?? c.value);
      return "";
    })
    .filter((v) => v.length > 0);
};

export default function Dashboard() {
  const { id } = useParams<{ id: string }>();
  const containerRef = useRef<HTMLDivElement>(null);

  const { loading: aclLoading, allowed, role, survey } = useSurveyAccess(
    id!,
    "analytics"
  );

  const [surveyDef, setSurveyDef] = useState<any>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Load survey JSON and responses
  useEffect(() => {
    if (!id) return;
    if (aclLoading) return;
    if (!allowed) {
      setError("You don’t have permission to view results for this survey.");
      return;
    }

    (async () => {
      try {
        setError(null);

        // Refresh latest survey doc
        const surveySnap = await getDoc(doc(db, "surveys", id));
        if (!surveySnap.exists()) throw new Error("Survey not found.");
        const raw = surveySnap.data() as any;
        const rawJson = raw?.json;
        if (!rawJson || !rawJson.pages)
          throw new Error("Survey definition is missing or invalid.");

        // --- Hydrate Firestore-linked dropdowns ---
        const json: any = JSON.parse(JSON.stringify(rawJson));
        type ToHydrate = { pageIdx: number; elIdx: number; listId: string };
        const toHydrate: ToHydrate[] = [];

        json.pages.forEach((page: any, pi: number) => {
          (page.elements || []).forEach((el: any, ei: number) => {
            const listId = el.firestoreListId || el.dataListId;
            if (el.type === "dropdown" && listId) {
              toHydrate.push({ pageIdx: pi, elIdx: ei, listId });
            }
          });
        });

        await Promise.all(
          toHydrate.map(async ({ pageIdx, elIdx, listId }) => {
            const itemsSnap = await getDocs(collection(db, "lists", listId, "items"));
            const labels = itemsSnap.docs
              .map((d) => String((d.data() as any)?.value ?? ""))
              .filter((s) => s.length > 0);

            json.pages[pageIdx].elements[elIdx].choices = labels.map((label) => ({
              value: label,
              text: label,
              valueName: label,
            }));

            delete json.pages[pageIdx].elements[elIdx].firestoreListId;
            delete json.pages[pageIdx].elements[elIdx].dataListId;
          })
        );

        setSurveyDef(json);

        // --- Fetch responses ---
        const respSnap = await getDocs(
          query(collection(db, "surveys", id, "responses"), orderBy("submittedAt", "asc"))
        );
        const flat = respSnap.docs
          .map((d) => d.data() as any)
          .filter((d) => d?.deleted !== true)
          .map((d) => d?.answers || {});
        setResponses(flat);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || "Failed to load dashboard.");
      }
    })();
  }, [id, allowed, aclLoading]);

  // Render analytics visualisation
  useEffect(() => {
    if (!surveyDef || !containerRef.current) return;

    containerRef.current.innerHTML = "";
    const surveyModel = new Model(surveyDef);
    const normalizeLabel = (val: string) => val.replace(/^\s*\d+\s*,\s*/g, "").trim();
    VisualizerBase.customColors = EDGE_COLOR_PALETTE;
    const options: IVisualizationPanelOptions = {
      allowHideQuestions: true,
      allowDynamicLayout: false,
      allowDragDrop: false,
      allowSortAnswers: false,
      answersOrder: "default",
      labelTruncateLength: -1,
    };
    const panel = new VisualizationPanel(surveyModel.getAllQuestions(), responses, options);
    const getColorsForLabels = (questionName: string | undefined, labels: string[]) => {
      const qDef = questionName ? findQuestionDef(surveyDef, questionName) : undefined;
      const orderedChoices = extractChoiceOrder(qDef);
      const palette = EDGE_COLOR_PALETTE;
      const colorMap = new Map<string, string>();
      let idx = 0;
      const orderedKeys: string[] = [];
      orderedChoices.forEach((label) => {
        const key = normalizeLabel(String(label));
        if (!orderedKeys.includes(key)) orderedKeys.push(key);
      });
      labels.forEach((label) => {
        const key = normalizeLabel(String(label));
        if (!orderedKeys.includes(key)) orderedKeys.push(key);
      });

      orderedKeys.forEach((key) => {
        if (!colorMap.has(key)) {
          colorMap.set(key, palette[idx % palette.length]);
          idx += 1;
        }
      });

      const colors = labels.map((label) => {
        const key = normalizeLabel(String(label));
        return colorMap.get(key) || palette[0];
      });
      return { colors, orderedChoices };
    };
    const applyColors = (sender: any, data: any) => {
      const labels = Array.isArray(data?.labels) ? data.labels : [];
      if (!labels.length || !sender?.question) return;
      let { colors, orderedChoices } = getColorsForLabels(sender.question.name, labels);
      data.colors = colors;
    };
    const attachVisualizer = (viz: any) => {
      if (!viz?.onAnswersDataReady?.add || viz.__edgeColorHooked) return;
      viz.__edgeColorHooked = true;

      if (typeof viz.getColors === "function") {
        const originalGetColors = viz.getColors.bind(viz);
        viz.getColors = () => {
          if (typeof viz.getLabels !== "function") return originalGetColors();
          const labels = viz.getLabels() || [];
          const fallback = originalGetColors(labels.length);
          const { colors } = getColorsForLabels(viz.question?.name, labels);
          return colors.map((color, idx) => color || fallback?.[idx] || EDGE_COLOR_PALETTE[0]);
        };
      }

      viz.onAnswersDataReady.add((sender: any, data: any) => applyColors(sender, data));
      if (typeof viz.getAnswersData === "function") {
        viz.getAnswersData()
          .then((data: any) => applyColors(viz, data))
          .catch(() => undefined);
      }
    };
    panel.onElementShown.add((sender: any, options: any) => {
      const viz = options?.element?.visualizer ?? options?.element;
      attachVisualizer(viz);
    });
    panel.onVisibleElementsChanged.add((sender: any, options: any) => {
      (options?.elements || []).forEach((el: any) => {
        const viz = el?.visualizer ?? el;
        attachVisualizer(viz);
      });
    });

    const plotlyHandler = (sender: any, plotOptions: any) => {
      const traces = plotOptions?.traces;
      if (!Array.isArray(traces) || traces.length === 0) return;
      const trace = traces[0] || {};
      const rawLabels =
        (Array.isArray(trace.customdata) && trace.customdata) ||
        (Array.isArray(trace.y) && trace.y) ||
        (Array.isArray(trace.x) && trace.x) ||
        [];
      const labels = rawLabels.map((label: any) => String(label));
      if (!labels.length) return;
      const questionName = sender?.question?.name || sender?.name;
      let { colors, orderedChoices } = getColorsForLabels(questionName, labels);
      traces.forEach((t: any) => {
        t.marker = t.marker || {};
        t.marker.color = colors;
      });
      if (plotOptions?.layout) {
        plotOptions.layout.colorway = colors;
      }
    };
    PlotlySetup.onPlotCreating.add(plotlyHandler);
    panel.render(containerRef.current);
    panel.visualizers.forEach(attachVisualizer);
    panel.visualizers.forEach((viz: any) => {
      if (typeof viz?.updateContent === "function") {
        viz.updateContent();
      } else if (typeof viz?.refresh === "function") {
        viz.refresh();
      }
    });
    return () => {
      panel.clear();
      if (PlotlySetup?.onPlotCreating?.remove) {
        PlotlySetup.onPlotCreating.remove(plotlyHandler);
      }
    };
  }, [surveyDef, responses]);

  // Loading & error UI
  if (aclLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!surveyDef) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Main dashboard UI
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        p: 2,
      }}
    >
      <PageHeader
        title={`Dashboard for “${surveyDef.title || id}”`}
        subtitle={`${responses.length} ${responses.length === 1 ? "response" : "responses"}`}
      />

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          p: 2,
        }}
        ref={containerRef}
      />

      {role && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Your access: {role}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
